import React, { useEffect, useState, useCallback, useRef } from "react";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { Loader } from "../Common/Loader";
import useVendorStore from "../HomePage/useVendorStore";
import { STATUS_BEGIN, STATUS_FAIL } from "utils/constants/others";
import ShowToast from "../Common/Toast";
import GenerateUpcModal from "./GenerateUpcModal";
import {
  hasCorePackagingDetails,
  isPackingLabelsV2Enabled,
  needsPackagingDetails,
} from "./embeddedUpcLabel";
import styles from "./DownloadLabels.module.scss";

const API_PAGE_SIZE = 1000;
const PAGE_SIZE = 20;

const DownloadLabels = () => {
  const navigate = useNavigate();
  const outletId = window.localStorage.getItem("outlet_id");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const debounceTimer = useRef(null);

  const [checkingComplianceFor, setCheckingComplianceFor] = useState(null);
  const [complianceError, setComplianceError] = useState("");
  const [legacyModalProduct, setLegacyModalProduct] = useState(null);
  const [packingLabelsV2Enabled, setPackingLabelsV2Enabled] = useState(null);

  // Vendor-level compliance, fetched without a product number. Until it is
  // complete the product cards stay locked.
  const [vendorCompliance, setVendorCompliance] = useState(null);
  const [loadingVendorCompliance, setLoadingVendorCompliance] = useState(true);

  const getVendorProductsFromPO = useVendorStore((s) => s.getVendorProductsFromPO);
  const getVendorProducts = useVendorStore((s) => s.getVendorProducts);
  const getPackagingLabelComplianceDetails = useVendorStore((s) => s.getPackagingLabelComplianceDetails);
  const vendorProductsFromPO = useVendorStore((s) => s.vendorProductsFromPO);
  const vendorProducts = useVendorStore((s) => s.vendorProducts);
  const vendorProductsTotalCount = useVendorStore((s) => s.vendorProductsTotalCount);
  const vendorProductsFromPOStatus = useVendorStore((s) => s.vendorProductsFromPOStatus);
  const vendorProductsStatus = useVendorStore((s) => s.vendorProductsStatus);

  // The app disables document scrolling globally. Restore it while this page
  // is mounted so the product list remains reachable on smaller viewports.
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  useEffect(() => {
    getVendorProductsFromPO({ vendor_id: outletId });
  }, []);

  // Called without product_number so the response carries the vendor record only.
  const loadVendorCompliance = useCallback(() => {
    setLoadingVendorCompliance(true);
    getPackagingLabelComplianceDetails(
      { vendor_id: outletId },
      (data) => {
        setLoadingVendorCompliance(false);
        setVendorCompliance(data?.vendor_details || {});
        setPackingLabelsV2Enabled(
          isPackingLabelsV2Enabled(data?.requirements)
        );
      },
      (errMsg) => {
        setLoadingVendorCompliance(false);
        setVendorCompliance({});
        setPackingLabelsV2Enabled(false);
        setComplianceError(errMsg || "Failed to load compliance details.");
      }
    );
  }, [outletId]);

  useEffect(() => {
    loadVendorCompliance();
  }, [loadVendorCompliance]);

  const complianceComplete = hasCorePackagingDetails(vendorCompliance);
  const canSelectProduct =
    packingLabelsV2Enabled === false ||
    (packingLabelsV2Enabled === true && complianceComplete);

  const goToVendorCompliance = () => {
    navigate("/download-labels/compliance", {
      state: {
        vendorDetails: vendorCompliance || {},
        requirements: {},
        returnTo: "/download-labels",
      },
    });
  };

  const isSearching = !!searchQuery.trim();

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!query.trim()) return;
    debounceTimer.current = setTimeout(() => {
      getVendorProducts({
        vendor_id: outletId,
        search: query.trim(),
        page_size: API_PAGE_SIZE,
      });
    }, 400);
  }, [outletId]);

  const isLoading =
    (isSearching ? vendorProductsStatus : vendorProductsFromPOStatus) === STATUS_BEGIN;
  const hasError =
    (isSearching ? vendorProductsStatus : vendorProductsFromPOStatus) === STATUS_FAIL;

  const allProducts = isSearching ? vendorProducts : vendorProductsFromPO;
  const totalPages = Math.ceil((allProducts?.length || 0) / PAGE_SIZE);
  const products = allProducts?.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  ) || [];

  const goToDownload = (product, requirements) => {
    navigate("/download-labels/download", {
      state: { product, requirements, returnTo: "/download-labels" },
    });
  };

  const handleSelectProduct = (product) => {
    if (checkingComplianceFor || !canSelectProduct) return;
    if (packingLabelsV2Enabled === false) {
      setLegacyModalProduct(product);
      return;
    }
    setComplianceError("");
    setCheckingComplianceFor(product?.product_number);
    getPackagingLabelComplianceDetails(
      { product_number: product?.product_number, vendor_id: outletId },
      (data) => {
        setCheckingComplianceFor(null);
        const requirements = data?.requirements || {};
        const vendorDetails = data?.vendor_details || {};
        if (!isPackingLabelsV2Enabled(requirements)) {
          setPackingLabelsV2Enabled(false);
          setLegacyModalProduct(product);
          return;
        }
        if (needsPackagingDetails(requirements, vendorDetails)) {
          navigate("/download-labels/compliance", {
            state: {
              product,
              requirements,
              vendorDetails,
              returnTo: "/download-labels",
            },
          });
        } else {
          goToDownload(product, requirements);
        }
      },
      (errMsg) => {
        setCheckingComplianceFor(null);
        setComplianceError(errMsg || "Failed to check compliance. Please try again.");
      }
    );
  };

  const formatPrice = (price) => {
    if (price == null) return "-";
    return `₹${Number(price).toLocaleString("en-IN")}`;
  };

  return (
    <React.Fragment>
      <Header showLogout={false} showProfile={true} />
      <div className={styles.container}>
        <div className={styles.titleRow}>
          <button className={styles.backBtn} onClick={() => navigate("/")}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5m0 0l6 6m-6-6l6-6" stroke="#1c1c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className={styles.pageTitle}>Download packaging labels</h1>
        </div>

        {!loadingVendorCompliance && packingLabelsV2Enabled === true && (
          <div
            className={styles.complianceCard}
            role="button"
            tabIndex={0}
            onClick={goToVendorCompliance}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") goToVendorCompliance();
            }}
          >
            <img
              className={styles.complianceIcon}
              src="/logos/packing_details_icon.png"
              alt=""
            />
            <div className={styles.complianceInfo}>
              <span className={styles.complianceTitle}>
                {complianceComplete
                  ? "Your packing details"
                  : "Packing details are pending"}
              </span>
              <span className={styles.complianceAction}>
                {complianceComplete ? "Edit" : "Complete"}
                <ChevronRight size={14} strokeWidth={2.4} aria-hidden />
              </span>
            </div>
          </div>
        )}

        <div className={styles.searchWrapper}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchQuery && (
            <span className={styles.clearBtn} onClick={() => { setSearchQuery(""); setCurrentPage(1); }}>
              ✕
            </span>
          )}
        </div>

        {isLoading ? (
          <Loader />
        ) : products?.length > 0 ? (
          <>
            <div className={styles.list}>
              {products?.map((product) => (
                <div
                  key={product?.product_number}
                  className={`${styles.card} ${canSelectProduct ? "" : styles.cardLocked}`}
                >
                  <div className={styles.cardLeft}>
                    <div className={styles.cardImageWrapper}>
                      {product?.image_url ? (
                        <img
                          src={product?.image_url}
                          alt={product?.product_name}
                          className={styles.cardImage}
                        />
                      ) : (
                        <div className={styles.cardImagePlaceholder}>
                          <span>No Image</span>
                        </div>
                      )}
                    </div>
                    <div className={styles.cardInfo}>
                      <span className={styles.productName}>{product?.product_name}</span>
                      <span className={styles.productNumber}>
                        #{product?.product_number}
                        {product?.price != null && (
                          <span className={styles.productPrice}>
                            {" · "}{formatPrice(product?.price)}/{product?.uom || "unit"}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className={styles.cardMeta}>
                    {product?.quantity != null && (
                      <div className={styles.metaBlock}>
                        <span className={styles.metaValue}>{product?.quantity} {product?.uom || "Count"}</span>
                        <span className={styles.metaLabel}>Order qty.</span>
                      </div>
                    )}
                    {product?.amount != null && (
                      <div className={styles.metaBlock}>
                        <span className={styles.metaValue}>{formatPrice(product?.amount)}</span>
                        <span className={styles.metaLabel}>Order amount</span>
                      </div>
                    )}
                  </div>

                  <button
                    className={styles.generateBtn}
                    onClick={() => handleSelectProduct(product)}
                    aria-label={`Download label for ${product?.product_name}`}
                    aria-busy={
                      checkingComplianceFor === product?.product_number
                    }
                    disabled={
                      !canSelectProduct ||
                      checkingComplianceFor === product?.product_number
                    }
                  >
                    <ChevronRight size={20} strokeWidth={2.4} aria-hidden />
                  </button>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ← Prev
                </button>
                <span className={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <span>No products found</span>
          </div>
        )}
      </div>
      <Footer />

      {hasError && (
        <ShowToast
          errorMsg="Failed to fetch products. Please try again."
          type="error"
        />
      )}

      {complianceError && (
        <ShowToast
          errorMsg={complianceError}
          type="error"
          onClose={() => setComplianceError("")}
        />
      )}

      <GenerateUpcModal
        visible={!!legacyModalProduct}
        item={legacyModalProduct}
        onClose={() => setLegacyModalProduct(null)}
      />
    </React.Fragment>
  );
};

export default DownloadLabels;
